import type {
  RollupWithTagsAndCountsAndRange,
  DataDumpAggregateType,
  ProductionSegmentType,
  ProcessGroup,
} from "types/types";
import { PnlCategoriesEnum } from "types/enums";
import { LineRolesEnum, getIsCypressRunning } from "constants/constants";
import { calcMetric } from "utils/metrics";
import { useIsStorybook } from "utils/customHooks/storyHooks";
import { useSelector } from "store/useSelector";
import { useVisibleProcessIds } from "utils/customHooks/genericHooks";
import { useFlightConfig } from "components/DataGetter/FlightConfig/useFlightConfig";
import { atomWithStorage } from "jotai/utils";
import { useAtom } from "jotai";
import { useCallback } from "react";

type getCallAndLastSegmentDetailsProps = {
  rollupMetric: RollupWithTagsAndCountsAndRange | null;
  preferredMetric: keyof DataDumpAggregateType;
  lastSegment: ProductionSegmentType | null;
  dataDumpsObjForFullRange: DataDumpAggregateType | null;
  isMidgardDDAEnabled: boolean;
};
export const getCallAndLastSegmentDetails = ({
  rollupMetric,
  preferredMetric,
  lastSegment,
  dataDumpsObjForFullRange,
  isMidgardDDAEnabled,
}: getCallAndLastSegmentDetailsProps) => {
  let metricDisplay;
  const metricUnits = "%";

  metricDisplay = calcMetric({
    rollupMetric,
    dataDumpKey: preferredMetric,
    dataDumpsObjForRange: dataDumpsObjForFullRange,
    isMidgardDDAEnabled,
  });
  metricDisplay =
    metricDisplay || metricDisplay === 0 ? metricDisplay.toFixed(0) : "--";

  let segment;
  // get the last segment that we have
  let pnlCategory = "loading";
  if (lastSegment) {
    segment = lastSegment;
    pnlCategory =
      segment && segment.pnl && segment.pnl.category
        ? (segment.pnl.category as PnlCategoriesEnum)
        : PnlCategoriesEnum.unknown;
  }
  let statusString = pnlCategory;
  if (segment && segment.tag && segment.tag.name) {
    statusString = segment.tag.name.toLowerCase();
    if (statusString === "no production scheduled") {
      statusString = "no prod sched";
    } else if (statusString === "waiting for material") {
      statusString = "wait for material";
    } else if (statusString === "waiting for operator") {
      statusString = "wait for operator";
    } else if (statusString === "waiting for setup op") {
      statusString = "wait for setup op";
    } else if (statusString === "material availability") {
      statusString = "material avail";
    }
  }
  return {
    segment,
    pnlCategory,
    statusString,
    metricDisplay,
    metricUnits,
  };
};

/** pass processId to determine isTileAheadBehindAllowedForProcess, otherwise it will be false */
export function useExpandedTileFlags(processId: string | null) {
  const [
    {
      general: {
        expandedProcessTileOperatorName: isOperatorNameAllowed,
        expandedProcessTileSkuId: isSkuIdAllowed,
        expandedProcessTileSkuName: isSkuNameAllowed,
        expandedProcessTileCount: isTileCountAllowed,
        expandedProcessTileTeamSize: isTeamSizeAllowed,
        expandedProcessTileAheadBehindProcesses: aheadBehindProcesses,
        expandedProcessTileTopLosses: isTopLossesAllowed,
      },
    },
  ] = useFlightConfig();

  const visibleProcessIds = useVisibleProcessIds();
  const aheadBehindProcessesVisible = aheadBehindProcesses.filter(id =>
    visibleProcessIds.includes(id)
  );

  const isTileButterflyAllowed = true; // for now, butterfly is allowed for all customers

  const isStorybook = useIsStorybook();

  return {
    isOperatorNameAllowed: isStorybook || isOperatorNameAllowed,
    isTileButterflyAllowed: isStorybook || isTileButterflyAllowed,
    isSkuIdAllowed: isStorybook || isSkuIdAllowed,
    isSkuNameAllowed: isStorybook || isSkuNameAllowed,
    isTileCountAllowed: isStorybook || isTileCountAllowed,
    isTeamSizeAllowed: isStorybook || isTeamSizeAllowed,
    isAnyTileAheadBehindAllowed:
      isStorybook || aheadBehindProcessesVisible.length > 0,
    isTileAheadBehindAllowedForProcess:
      isStorybook ||
      Boolean(processId && aheadBehindProcessesVisible.includes(processId)),
    isCustomerWithExpandableTile:
      // isTileButterflyAllowed || // * don't check for butterfly since it defaults to true
      isStorybook ||
      isOperatorNameAllowed ||
      isSkuIdAllowed ||
      isSkuNameAllowed ||
      isTileCountAllowed ||
      aheadBehindProcessesVisible.length > 0,
    isTopLossesAllowed: isStorybook || isTopLossesAllowed,
  };
}

export function useGetLineRole(pgKey: string) {
  const constraintProcessIds = useSelector(
    state => state.legacy.constraintProcessIds[pgKey]
  );
  const leaderProcessId = useSelector(
    state => state.legacy.leaderProcessId[pgKey]
  );

  return useCallback(
    processId =>
      processId === leaderProcessId
        ? LineRolesEnum.LEADER
        : constraintProcessIds?.includes(processId)
          ? LineRolesEnum.CONSTRAINT
          : LineRolesEnum.STANDARD,
    [constraintProcessIds, leaderProcessId]
  );
}

type CachedProcessGroupsType = {
  [ckey: string]: { [workScheduleId: string]: ProcessGroup[] };
};
const cachedProcessGroupsAtom = atomWithStorage<CachedProcessGroupsType>(
  "cachedProcessGroups",
  {}
);
/** cache all processes for all customers, to eliminate subsequent loads after tiles have loaded once */
export function useCachedProcessGroups(): [
  CachedProcessGroupsType,
  React.Dispatch<React.SetStateAction<CachedProcessGroupsType>>,
] {
  const [cachedProcessGroups, setCachedProcessGroups] = useAtom(
    cachedProcessGroupsAtom
  );
  const isStorybook = useIsStorybook();
  if (getIsCypressRunning() || isStorybook) {
    return [{}, _ => null] as [
      CachedProcessGroupsType,
      React.Dispatch<React.SetStateAction<CachedProcessGroupsType>>,
    ];
  }
  return [cachedProcessGroups, setCachedProcessGroups];
}
